import { useState } from "react";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { Input } from "./ui/input";
import { toast } from "sonner";
import axios from "axios";
import { smoothScrollTo } from "../utils/smoothScroll";
import { useNavigate } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tap-pro-hub/artifacts/gmy7q88s_Photoroom_20260409_140507.png";
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const footerLinks = {
  services: [
    { name: "Line Cleaning", href: "#services" },
    { name: "System Installation", href: "#services" },
    { name: "Maintenance", href: "#services" },
    { name: "Emergency Repairs", href: "#services" },
  ],
  resources: [
    { name: "Industry Resources", href: "#resources" },
    { name: "What's Trending", href: "#trends" },
    { name: "Why Tibs Taps", href: "#why-us" },
    { name: "Contact Us", href: "#contact" },
    { name: "Careers", href: "/careers" },
  ],
};

export default function Footer() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubscribing(true);
    try {
      await axios.post(`${API_URL}/newsletter`, { email });
      toast.success("Subscribed! You'll receive industry insights and updates.");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info("You're already subscribed!");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubscribing(false);
    }
  };

  const scrollToSection = (href) => {
    if (href.startsWith("/")) {
      navigate(href);
    } else {
      smoothScrollTo(href);
    }
  };

  return (
    <footer className="bg-[#0A0A0A] border-t border-[#27272A]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <div className="bg-white rounded-full border-4 border-black w-28 h-28 flex items-center justify-center">
                <img
                  src={LOGO_URL}
                  alt="Tibs Taps"
                  className="w-20 h-auto -translate-x-1"
                  data-testid="footer-logo"
                />
              </div>
            </div>
            <p className="text-[#A1A1AA] text-sm mb-6">
              New England's premier draft beer line service company. 
              Keeping your taps flowing perfectly since 2010.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/TibsTaps/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-[#141414] border border-[#27272A] flex items-center justify-center text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-all"
                data-testid="footer-social-facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/explore/locations/74093362/tibs-taps/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-[#141414] border border-[#27272A] flex items-center justify-center text-[#A1A1AA] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 transition-all"
                data-testid="footer-social-instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-heading text-white mb-6">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="footer-link text-sm"
                    data-testid={`footer-link-service-${link.name}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources Links */}
          <div>
            <h4 className="font-heading text-white mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="footer-link text-sm"
                    data-testid={`footer-link-resource-${link.name}`}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter & Contact */}
          <div>
            <h4 className="font-heading text-white mb-6">Stay Updated</h4>
            <form onSubmit={handleNewsletterSubmit} className="mb-8">
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="input-dark flex-1"
                  required
                  data-testid="newsletter-input"
                />
                <button
                  type="submit"
                  disabled={isSubscribing}
                  className="btn-gold px-4 py-2 flex items-center justify-center"
                  data-testid="newsletter-submit"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </form>

            <div className="space-y-3">
              <a
                href="tel:603-548-9932"
                className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#D4AF37] text-sm transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-4 h-4" />
                603-548-9932
              </a>
              <a
                href="mailto:tibstaps@gmail.com"
                className="flex items-center gap-3 text-[#A1A1AA] hover:text-[#D4AF37] text-sm transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4" />
                tibstaps@gmail.com
              </a>
              <div className="flex items-start gap-3 text-[#A1A1AA] text-sm">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Londonderry, NH
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-[#27272A] flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-[#A1A1AA] text-sm" data-testid="footer-copyright">
            © {new Date().getFullYear()} Tibs Taps. All rights reserved.
          </p>
          <p className="text-[#A1A1AA] text-sm">
            Proudly serving New Hampshire & New England
          </p>
        </div>
      </div>
    </footer>
  );
}
